import React, { useEffect, useState } from "react";
import LegalLayout from "@/components/LegalLayout";

/**
 * Public status page. Pings /api/health every 30s and reports the
 * current operational state of the API + database. Designed to be
 * visible to anyone (signed in or not) so users have a place to look
 * when they suspect an outage before contacting support.
 */
export default function StatusPage() {
  const [state, setState] = useState({ status: "checking", checked_at: null, latency_ms: null, db: null });
  const [history, setHistory] = useState([]); // last 10 pings

  useEffect(() => {
    let cancelled = false;
    async function ping() {
      const t0 = performance.now();
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/health`, { method: "GET" });
        const data = await res.json().catch(() => ({}));
        const latency_ms = Math.round(performance.now() - t0);
        if (cancelled) return;
        const next = {
          status: res.ok && data?.ok ? "operational" : "degraded",
          checked_at: new Date().toISOString(),
          latency_ms,
          db: data?.db || null,
        };
        setState(next);
        setHistory((h) => [next, ...h].slice(0, 10));
      } catch {
        if (cancelled) return;
        const next = { status: "down", checked_at: new Date().toISOString(), latency_ms: null, db: null };
        setState(next);
        setHistory((h) => [next, ...h].slice(0, 10));
      }
    }
    ping();
    const interval = setInterval(ping, 30_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const colors = {
    operational: { bg: "#0E0E0E", fg: "#FFFFFF", dot: "#16A34A", label: "All systems operational" },
    degraded: { bg: "#FFF8E5", fg: "#8A6A00", dot: "#C9A227", label: "Degraded performance" },
    down: { bg: "#FCE9EA", fg: "#7A1014", dot: "#C1272D", label: "Service interruption" },
    checking: { bg: "#F5F5F5", fg: "#6B6B6B", dot: "#9CA3AF", label: "Checking…" },
  };
  const c = colors[state.status] || colors.checking;

  return (
    <LegalLayout
      eyebrow="Operational status"
      title="System status"
      lastUpdated={state.checked_at ? new Date(state.checked_at).toLocaleString() : ""}
    >
      <div
        data-testid="status-banner"
        className="not-prose rounded-2xl p-6 mb-8 flex items-center gap-4"
        style={{ background: c.bg, color: c.fg }}
      >
        <span className="inline-block h-3 w-3 rounded-full" style={{ background: c.dot, boxShadow: `0 0 0 4px ${c.dot}33` }} />
        <div className="flex-1">
          <p className="text-sm font-semibold">{c.label}</p>
          {state.latency_ms != null && (
            <p className="text-xs opacity-80 mt-0.5">API response time: {state.latency_ms}ms</p>
          )}
        </div>
      </div>

      <h2>Components</h2>
      <ul>
        <li>
          <strong>API:</strong>{" "}
          <span data-testid="status-api">{state.status === "operational" ? "Operational" : state.status === "checking" ? "Checking" : state.status === "down" ? "Down" : "Degraded"}</span>
        </li>
        <li>
          <strong>Database:</strong>{" "}
          <span data-testid="status-db">{state.db === "ok" ? "Operational" : state.db === null ? "—" : "Issue detected"}</span>
        </li>
        <li>
          <strong>Payments:</strong> Operational (Stripe-managed — see{" "}
          <a href="https://status.stripe.com" target="_blank" rel="noopener noreferrer">status.stripe.com</a>)
        </li>
        <li>
          <strong>Email delivery:</strong> Operational (Resend-managed — see{" "}
          <a href="https://resend-status.com" target="_blank" rel="noopener noreferrer">resend-status.com</a>)
        </li>
      </ul>

      <h2>Recent checks</h2>
      <p className="text-xs text-[#6B6B6B]">Last 10 health probes from this browser. Auto-refreshes every 30s.</p>
      <div className="not-prose flex items-end gap-1 mt-3 h-12">
        {history.length === 0 && (
          <span className="text-xs text-[#9CA3AF]">No data yet…</span>
        )}
        {history.slice().reverse().map((h, i) => (
          <span
            key={i}
            title={`${h.status} · ${h.latency_ms ?? "?"}ms · ${h.checked_at?.slice(11, 19) || ""}`}
            className="flex-1 rounded-sm"
            style={{
              minHeight: "20%",
              height: `${Math.min(100, ((h.latency_ms || 0) / 5))}%`,
              background:
                h.status === "operational" ? "#16A34A" :
                  h.status === "down" ? "#C1272D" : "#C9A227",
            }}
          />
        ))}
      </div>

      <h2 style={{ marginTop: "2rem" }}>Subscribe</h2>
      <p>
        For incident notifications during launch, send us your email at{" "}
        <a href="mailto:support@armstrongk9registry.com">support@armstrongk9registry.com</a>{" "}
        with subject "Status alerts subscribe" and we'll add you to the on-call distribution list.
      </p>
    </LegalLayout>
  );
}
